<template>
	<section
		class="hero"
		:class="{ 'has-bg': backgroundImage }"
		:style="heroVars"
	>
		<div class="hero__overlay"></div>

		<div class="container hero__container">
			<div class="row hero__row">
				<!-- LEFT -->
				<div class="col-12 col-md-6 hero__left">
					<div>
						<h1
							v-if="title"
							class="hero__title headline-l"
							v-html="titleWithAccent"
						></h1>

						<p
							v-if="description"
							class="text-l hero__desc"
							v-html="description"
						></p>

						<p
							v-if="subDescription"
							class="text-l hero__sub__desc"
							v-html="subDescription"
						></p>

						<div v-if="showStores" class="hero__stores">
							<slot name="stores">
								<store-button :version="'apple'" />
								<store-button :version="'google'" />
							</slot>
						</div>
					</div>
				</div>

				<!-- RIGHT -->
				<div class="col-12 col-md-6 hero__right">
					<div class="hero__phoneWrap">
						<img
							v-if="phoneImage"
							class="hero__phone"
							:src="phoneImage"
							:alt="phoneAlt"
						/>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script>
export default {
	name: 'HeroJumbotron',
	props: {
		backgroundImage: { type: String, default: '' },
		mobileBackgroundImage: { type: String, default: '' },
		overlayOpacity: { type: [Number, String], default: 0.6 },

		title: { type: String, default: '' },
		description: { type: String, default: '' },
		subDescription: { type: String, default: '' },

		titleColor: { type: String, default: '#FFFFFF' },
		accentColor: { type: String, default: '#00DDA9' },
		textColor: { type: String, default: 'rgba(255,255,255,0.85)' },

		showStores: { type: Boolean, default: true },

		phoneImage: { type: String, default: '' },
		phoneAlt: { type: String, default: 'Phone preview' },

		minHeight: { type: [Number, String], default: 622 },
	},
	computed: {
		heroVars() {
			return `
        --bg-image: url('${this.backgroundImage}');
        --bg-image-mobile: url('${
					this.mobileBackgroundImage || this.backgroundImage
				}');
        --overlay-opacity: ${this.overlayOpacity};
        --title-color: ${this.titleColor};
        --accent-color: ${this.accentColor};
        --text-color: ${this.textColor};
        --min-height: ${Number(this.minHeight)}px;
      `;
		},
		titleWithAccent() {
			if (!this.title) return '';
			const parts = this.title.split('\n');
			if (parts.length === 1) return parts[0];
			return `${parts[0]}<br><span class="accent">${parts
				.slice(1)
				.join('<br>')}</span>`;
		},
	},
};
</script>

<style lang="scss" scoped>
.hero {
	position: relative;
	min-height: var(--min-height);
	overflow: hidden;
	background: #0a1433;
	&.has-bg {
		background-image: var(--bg-image);
		background-size: cover;
		background-position: center;
		background-repeat: no-repeat;
		position: relative;
		@media (max-width: $breakpoint-sm) {
			background-image: var(--bg-image-mobile);

			&::before {
				content: '';
				position: absolute;
				inset: 0;
				background: linear-gradient(
					rgba(4, 4, 40, 1),
					rgba(21, 26, 192, 0),
					rgba(13, 13, 95, 1),
					rgba(4, 4, 40, 1)
				);
				pointer-events: none;
				z-index: 1;
			}
		}
		@media (max-width: 367px) {
			&::before {
				background: linear-gradient(
					rgba(4, 4, 40, 1),
					rgba(21, 26, 192, 0),
					rgba(13, 13, 95, 1),
					rgba(4, 4, 40, 1),
					rgba(4, 4, 40, 1)
				);
			}
		}
	}

	&::after {
		content: '';
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		height: 120px;
		pointer-events: none;
		background: linear-gradient(180deg, rgba(10, 20, 51, 0) 0%, #0a1433 100%);
	}
}

.hero__overlay {
	position: absolute;
	inset: 0;
	pointer-events: none;
}

.hero__container {
	position: relative;
	padding-top: 48px;
}

.hero__row {
	min-height: var(--min-height);
	display: flex;
	flex-direction: row;
	@media (max-width: $breakpoint-sm) {
		flex-direction: column-reverse;
		min-height: unset;
	}
}

.hero__left {
	padding-top: 117px;
	@media (max-width: $breakpoint-sm) {
		z-index: 1;
		padding-top: 0px;
		text-align: center;
		align-items: center;
		display: flex;
		flex-direction: column;
	}
}

.hero__title {
	margin: 0;
	color: var(--title-color);
	@media (max-width: $breakpoint-md) {
		font-size: 32px;
		line-height: 40px;
	}
	@media (max-width: $breakpoint-sm) {
		font-size: 28px;
		line-height: 35px;
		font-family: 'HKNova-SemiBold', sans-serif;
	}
}

:deep(.hero__title .accent) {
	color: var(--accent-color) !important;
}

.hero__desc {
	color: var(--text-color);
	max-width: 327px;
	font-size: 18px;
	line-height: 30px;
	@media (max-width: $breakpoint-md) {
		font-size: 16px;
		line-height: 24px;
	}
	@media (max-width: $breakpoint-sm) {
		font-size: 15px;
		line-height: 22px;
		max-width: 100%;
	}
	@media (max-width: $breakpoint-xs) {
		display: none;
	}
}

.hero__sub__desc {
	color: var(--text-color);
	margin-top: 42px;
	font-size: 16px;
	line-height: 24px;
	@media (max-width: $breakpoint-md) {
		font-size: 15px;
		line-height: 22px;
	}
	@media (max-width: $breakpoint-sm) {
		padding-left: 100px;
		padding-right: 100px;
		font-size: 14px;
		line-height: 20px;
		margin-top: 12px;
		font-family: 'HKNova-SemiBold', sans-serif;
	}
	@media (max-width: $breakpoint-xs) {
		font-size: 13px;
		line-height: 18px;
	}
}

.hero__stores {
	margin-top: 31px;
	display: flex;
	gap: 20px;
	align-items: center;
	flex-wrap: wrap;
	@media (max-width: $breakpoint-sm) {
		justify-content: center;
		margin-top: 25px;
	}
}

.hero__right {
	display: flex;
	align-items: flex-end;
}

.hero__phoneWrap {
	width: 100%;
	display: flex;
	justify-content: flex-end;
	@media (max-width: $breakpoint-sm) {
		justify-content: center;
	}
}

.hero__phone {
	width: 395px;
	height: auto;
	filter: drop-shadow(0 20px 60px rgba(0, 0, 0, 0.35));
	@media (max-width: $breakpoint-md) {
		width: 320px;
	}
	@media (max-width: $breakpoint-sm) {
		width: 265px;
	}
}
</style>
